import { LocationEnduserApi } from '@/apis';
import { ResponseData } from '@/types/http';
import { TableState } from '@tanstack/react-table';
import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import {
    ListLocationBikeResponse,
    LocationBike
} from '@/types/http-payload/locationBike';
import { LocationDetailEuResponse } from '@/types/http-payload/location';
import { isPathEk } from '@/utils/routes';
import { COMMON_TYPE } from '@/types/enums';

export default class LocationEnduserStore extends BaseStore {
    api: LocationEnduserApi;

    defaultPagingArea: TableState = {
        pagination: {
            pageIndex: 0,
            pageSize: 6
        },
        sorting: [{}]
    } as TableState;

    defaultPagingCurrent: TableState = {
        pagination: {
            pageIndex: 0,
            pageSize: 8
        },
        sorting: [{}]
    } as TableState;

    // Search by area
    locationsArea: LocationBike[] = [];
    pagingLocationsArea: TableState = this.defaultPagingArea;
    totalRecordLocationsArea: number = 0;
    totalPageLocationsArea: number = 0;

    locationsAreaPinMap: LocationBike[] = [];

    // Search by current
    locationsCurrent: LocationBike[] = [];
    pagingLocationsCurrent: TableState = this.defaultPagingCurrent;
    totalRecordLocationsCurrent: number = 0;

    // Location detail
    locationDetail: LocationDetailEuResponse | undefined = undefined;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            // Search by area
            locationsArea: observable,
            defaultPagingArea: observable,
            pagingLocationsArea: observable,
            totalRecordLocationsArea: observable,
            totalPageLocationsArea: observable,
            locationsAreaPinMap: observable,
            getLocationsArea: flow.bound,
            getLocationsAreaPinMap: flow.bound,
            // Search by current
            locationsCurrent: observable,
            pagingLocationsCurrent: observable,
            totalRecordLocationsCurrent: observable,
            getLocationsCurrent: flow.bound,
            // Location detail
            locationDetail: observable,
            getLocationDetail: flow.bound
        });
        this.api = new LocationEnduserApi();
    }

    // #region Search by area
    *getLocationsArea(searchParams?: any, paging?: TableState) {
        const pathname = window.location.pathname;
        const isEk = isPathEk(pathname);
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingLocationsArea
                ),
                ...searchParams,
                type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
            };

            const res: ResponseData<ListLocationBikeResponse> =
                yield this.rootStore.apiStore.call(
                    this.api,
                    this.api.getLocationRecents,
                    payload
                );
            if (res.ok) {
                this.locationsArea = res.data.elements;
                this.pagingLocationsArea = {
                    ...this.pagingLocationsArea,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecordLocationsArea = res.data.paginate.totalRecord;
                this.totalPageLocationsArea = res.data.paginate.totalPage;

                return true;
            }
            return false;
        } catch (error) { }
    }

    *getLocationsAreaPinMap(payload?: any) {
        const pathname = window.location.pathname;
        const isEk = isPathEk(pathname);
        try {
            const res: ResponseData<ListLocationBikeResponse> =
                yield this.rootStore.apiStore.call(
                    this.api,
                    this.api.getLocationRecents,
                    {
                        ...payload,
                        type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
                    }
                );
            if (res.ok) {
                this.locationsAreaPinMap = res.data.elements;
                return true;
            }
            return false;
        } catch (error) { }
    }
    // #endregion Search by area

    // #region Search by current
    *getLocationsCurrent(searchParams?: any, paging?: TableState) {
        const pathname = window.location.pathname;
        const isEk = isPathEk(pathname);
        try {
            const payload = {
                ...this.convertPagingFromTableToRequest(
                    paging ?? this.pagingLocationsCurrent
                ),
                type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
                ...searchParams
            };
            const res: ResponseData<ListLocationBikeResponse> =
                yield this.rootStore.apiStore.call(
                    this.api,
                    this.api.getLocationRecents,
                    payload
                );
            if (res.ok) {
                this.locationsCurrent = res.data.elements;
                this.pagingLocationsCurrent = {
                    ...this.pagingLocationsCurrent,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(
                        res.data.paginate
                    )
                };
                this.totalRecordLocationsCurrent = res.data.paginate.totalRecord;

                return true;
            }
            return false;
        } catch (error) { }
    }

    // #endregion Search by current

    // #region Location detail
    *getLocationDetail(payload) {
        try {
            const res: ResponseData<LocationDetailEuResponse> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getLocationDetail,
                payload
            );
            if (res.ok) {
                this.locationDetail = res.data;
                return res;
            }
        } catch (error) { }
    }
    // #endregion Location detail

    clean(): void {
        super.clean();

        this.locationsArea = [];
        this.pagingLocationsArea = this.defaultPagingArea;
        this.totalRecordLocationsArea = 0;
        this.totalPageLocationsArea = 0;
        this.locationsAreaPinMap = [];

        this.locationsCurrent = [];
        this.pagingLocationsCurrent = this.defaultPagingCurrent;
        this.totalRecordLocationsCurrent = 0;

        this.locationDetail = undefined;
    }
}
